/**
 * A value over time as one line: it wears the colour of its own stretch — gain when it ends above
 * where it began, loss when below — and it can be walked: the point under the pointer is handed to
 * `onScrub`, so the page's own number follows the finger.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { cx, usd } from './kit'

/* ── the chart ───────────────────────────────────────────────────────── */

export interface Level {
  y: number
  label: string
  tone: 'up' | 'down' | 'quiet'
}
const TONE = { up: 'var(--color-ws-up)', down: 'var(--color-ws-down)', quiet: '#686868' }

/**
 * A price line with its scale and the levels that matter drawn across it. The line wears the colour of
 * its own range — gain when it ends above where it began, loss when below — and it can be walked:
 * the point under the pointer is handed to `onScrub`, so the page's own number follows the finger.
 */
export function Chart({ points, labels, height = 240, levels = [], band, fmt = (v: number) => usd(v), onScrub, live, bare }: { points: number[]; labels: string[]; height?: number; levels?: Level[]; band?: [number, number]; fmt?: (v: number) => string; onScrub?: (i: number | null) => void; live?: boolean; /** No scale and no dates: the line alone, with a dotted line at the level it started from. */ bare?: boolean }) {

  const box = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(0)
  const [at, setAtRaw] = useState<number | null>(null)
  const setAt = (i: number | null) => (setAtRaw(i), onScrub?.(i))
  const id = useId()
  const ink = points[points.length - 1] >= points[0] ? 'var(--color-ws-up)' : 'var(--color-ws-down)'
  useEffect(() => {
    const el = box.current
    if (!el) return
    const watch = new ResizeObserver(() => setW(el.clientWidth))
    watch.observe(el)
    return () => watch.disconnect()
  }, [])
  const n = points.length
  const AXIS = bare ? 0 : 58 // the scale on the right
  const TOP = bare ? 26 : 10
  const FOOT = bare ? 10 : 24 // the dates under the line
  const all = [...points, ...levels.map((l) => l.y), ...(band ?? [])]
  const min = Math.min(...all)
  const max = Math.max(...all)
  const pad = (max - min || 1) * 0.08
  const lo = min - pad
  const hi = max + pad
  const pw = Math.max(0, w - AXIS)
  const ph = height - TOP - FOOT
  const x = (i: number) => (n > 1 ? (i / (n - 1)) * pw : 0)
  const y = (v: number) => TOP + (1 - (v - lo) / (hi - lo)) * ph
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p).toFixed(1)}`).join('')
  const ticks = [0, 1, 2, 3].map((i) => lo + ((hi - lo) * (i + 0.5)) / 4)
  const dates = n > 2 ? [0, Math.floor((n - 1) / 2), n - 1] : []
  const move = (px: number) => setAt(pw > 0 ? Math.max(0, Math.min(n - 1, Math.round((px / pw) * (n - 1)))) : null)
  return (
    <div
      ref={box}
      className="relative w-full touch-pan-y select-none"
      style={{ height }}
      onPointerMove={(e) => move(e.clientX - e.currentTarget.getBoundingClientRect().left)}
      onPointerDown={(e) => move(e.clientX - e.currentTarget.getBoundingClientRect().left)}
      onPointerLeave={() => setAt(null)}
    >
      {w > 0 && (
        <svg width={w} height={height} className="block overflow-visible" aria-hidden>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={ink} stopOpacity="0.16" />
              <stop offset="1" stopColor={ink} stopOpacity="0" />
            </linearGradient>
          </defs>
          {bare && <line x1={0} x2={pw} y1={Math.round(y(points[0])) + 0.5} y2={Math.round(y(points[0])) + 0.5} stroke="#4a4a4a" strokeDasharray="1 5" strokeLinecap="round" />}
          {!bare && ticks.map((t, i) => (
            <g key={i}>
              <line x1={0} x2={pw} y1={Math.round(y(t)) + 0.5} y2={Math.round(y(t)) + 0.5} stroke="var(--color-ws-hair)" />
              <text x={w} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#686868" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt(t)}
              </text>
            </g>
          ))}
          {band && <rect x={0} width={pw} y={y(band[1])} height={Math.max(0, y(band[0]) - y(band[1]))} fill="var(--color-ws-up)" opacity="0.07" />}
          {/* (keyed by its own shape: a new range is drawn in, not swapped) */}
          <g key={`${n}:${points[0].toFixed(2)}`}>
            <path d={`${line}L${pw.toFixed(1)} ${TOP + ph}L0 ${TOP + ph}Z`} fill={`url(#${id})`} className="animate-ws-fade" />
            <path d={line} fill="none" stroke={ink} strokeWidth={bare ? 1.75 : 1.5} strokeLinejoin="round" strokeLinecap="round" pathLength={1} strokeDasharray="1" className="animate-ws-draw" />
          </g>
          {at == null && (
            <>
              {live && <circle cx={x(n - 1)} cy={y(points[n - 1])} r="7" fill={ink} opacity="0.25" className="animate-ws-pulse" />}
              <circle cx={x(n - 1)} cy={y(points[n - 1])} r="3" fill={ink} />
            </>
          )}
          {levels.map((l) => (
            <g key={l.label}>
              <line x1={0} x2={pw} y1={Math.round(y(l.y)) + 0.5} y2={Math.round(y(l.y)) + 0.5} stroke={TONE[l.tone]} strokeDasharray="3 4" opacity="0.8" />
              <text x={6} y={y(l.y) - 5} fontSize="11" fill={TONE[l.tone]}>
                {l.label} {fmt(l.y)}
              </text>
            </g>
          ))}
          {!bare && dates.map((i, k) => (
            <text key={i} x={x(i)} y={height - 6} textAnchor={k === 0 ? 'start' : k === 2 ? 'end' : 'middle'} fontSize="11" fill="#686868">
              {labels[i]}
            </text>
          ))}
          {at != null && (
            <>
              <line x1={Math.round(x(at)) + 0.5} x2={Math.round(x(at)) + 0.5} y1={TOP} y2={TOP + ph} stroke="var(--color-ws-rule)" />
              <circle cx={x(at)} cy={y(points[at])} r="4" fill={ink} stroke={bare ? 'var(--color-ws-bg)' : 'var(--color-ws-panel)'} strokeWidth="2" />
            </>
          )}
        </svg>
      )}
      {at != null && (
        <div className={cx('pointer-events-none absolute top-0 flex -translate-x-1/2 items-baseline gap-2 whitespace-nowrap text-[12px] leading-4 tracking-normal', !bare && 'rounded-[6px] border border-[#2a2a2a] bg-[#141414] px-2 py-1')} style={{ left: Math.max(64, Math.min(pw - 64, x(at))) }}>
          {!onScrub && <span className="font-medium tabular-nums text-ws-fg">{fmt(points[at])}</span>}
          <span className="text-ws-fg-3">{labels[at]}</span>
        </div>
      )}
    </div>
  )
}

/** The ranges of a chart: plain words under the line, the chosen one wearing the line's own colour. */
